import { useCallback, useEffect } from 'react';
import { useAppStore } from '../../stores/useAppStore';

/**
 * Warn before losing unsaved changes.
 *
 * - `beforeunload` covers refresh and tab close.
 * - The app store's `leaveGuard` is consulted by the navigation modal before an
 *   in-app route change. The app uses a plain BrowserRouter, so browser
 *   back/forward cannot be intercepted; this is the best available coverage.
 */
export function useUnsavedChangesGuard(isDirty, message = 'You have unsaved changes. Leave this page?') {
  const setLeaveGuard = useAppStore((s) => s.setLeaveGuard);

  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, message]);

  useEffect(() => {
    setLeaveGuard(() => !isDirty || window.confirm(message));
    return () => setLeaveGuard(null);
  }, [isDirty, message, setLeaveGuard]);

  return useCallback(() => !isDirty || window.confirm(message), [isDirty, message]);
}
